import { getNFTActor } from '@funded-labs/dab-js'
import { Principal } from '@dfinity/principal'

import {
  ApproveErc20Params,
  ApproveParams,
  BTCSendOrdinalParams,
  BTCSendParams,
  CustomApproveErc20Params,
  EthCallContractParams,
  MintCkEthParams,
  SendErc20Params,
  SendEthParams,
  SignMessageParams,
  SolSendParams,
  WalletAction,
  walletActions,
  XTCBurnParams,
} from '../../interfaces/walletActions'
import {
  hasVault,
  isInitialized,
  isPasswordSet,
  isVaultUnlocked,
  keychainManager,
} from '../../keyring'
import { KeychainType } from '../../keyring/keychains/IKeychain'
import sendToken from '../../utils/tokenActions/sendToken'

import { logger } from '../../logger'
import getAgent from '../../utils/getAgent'
import approveToken from '../../utils/tokenActions/approveToken'
import burnXTC from '../../utils/tokenActions/burnXTC'
import { getChainFusionClient } from '../../utils/getChainFusionClient'
import { toHex } from '@dfinity/agent'

export type WalletActionArguments = {
  action: WalletAction
  wallet: string
  payload: any
}

const handleApproveToken = async (params: ApproveParams, currentWallet: string) => {
  const { tokenCanisterId, standard, amount, spender } = params
  const identity = await keychainManager.getIdentity(currentWallet)

  const approveResponse = await approveToken({
    identity,
    approveParams: {
      tokenCanisterId,
      standard,
      amount,
      spender,
    },
  }).catch((e) => {
    logger.error('Approve error', { originalError: e })
  })

  return approveResponse
}

const handleBurnXTC = async (params: XTCBurnParams, currentWallet: string) => {
  const identity = await keychainManager.getIdentity(currentWallet)

  return burnXTC(params, getAgent({ identity }))
}

const signMessage = async ({ principalId, message }: SignMessageParams) => {
  const identity = await keychainManager.getIdentity(principalId)
  // @ts-ignore sign wants ArrayBuffer for some reason, but works with strings
  const signature = await identity.sign(message)

  return {
    publicKey: toHex(identity.getPublicKey().toDer()),
    signature: toHex(signature),
  }
}

export const processWalletAction = async ({ action, payload, wallet }: WalletActionArguments) => {
  let response

  switch (action) {
    case walletActions.status:
      const isReady = isInitialized()
      const _hasVault = isReady && (await hasVault())
      const unlocked = _hasVault && isVaultUnlocked()
      const hasPassword = _hasVault && (await isPasswordSet())
      response = {
        hasVault: _hasVault,
        unlocked,
        hasPassword,
        isReady,
      }
      break
    case walletActions.unlock:
      try {
        response = await keychainManager.unlock(payload.password)
      } catch (e) {
        response = false
      }

      break
    case walletActions.create:
      const keychain = await keychainManager.addNewKeychain()
      const mnemonic = await keychain.exportKeychain()
      const principals = await keychain.getPrincipals()
      response = { mnemonic, principal: principals[0].toString() }
      break
    case walletActions.import:
      const importedKeychain = await keychainManager.addNewKeychain({
        mnemonic: payload.mnemonic,
        imported: true,
        accountsEnabled: 1,
        type: KeychainType.HDKeychain,
      })
      const newPrincipals = await importedKeychain.getPrincipals()
      response = { principal: newPrincipals[0].toString() }
      break
    case walletActions.lock:
      response = await keychainManager.lock()
      break
    case walletActions.changePassword:
      response = await keychainManager.setPassword(payload.password)
      break
    case walletActions.clearKeychain:
      response = await keychainManager.clearKeychain()
      break
    case walletActions.transferToken:
      const { tokenCanisterId, standard, amount, receiver, memo, tokenId, chain } = payload
      const identity = await keychainManager.getIdentity(wallet)

      try {
        const transferResponse = await sendToken({
          identity,
          sendParams: {
            tokenCanisterId,
            tokenId,
            chain,
            standard,
            amount,
            receiver,
            memo,
          },
        })
        response = transferResponse
      } catch (e: any) {
        logger.error('Failed to send token', {
          originalError: e,
          standard,
          tokenCanisterId,
        })

        response = { error: e.msg || e.toString() }
      }

      break
    case walletActions.sendNFT:
      const nftSendIdentity = await keychainManager.getIdentity(wallet)

      const agent = getAgent({ identity: nftSendIdentity })

      const {
        canisterId: nftCanisterId,
        standard: nftStandard,
        receiverPrincipal,
        nftIndex,
      } = payload

      const actor = getNFTActor({
        agent,
        canisterId: nftCanisterId,
        standard: nftStandard,
      })

      try {
        const nftSendResponse = await actor.transfer(
          Principal.from(receiverPrincipal),
          parseInt(nftIndex, 10)
        )
        response = nftSendResponse
      } catch (e) {
        response = { error: (e as Error).toString() }
      }

      break
    case walletActions.approveToken:
      response = await handleApproveToken(payload, wallet)
      break
    case walletActions.transferEth:
      const ethHelperClient = await getChainFusionClient(wallet)

      response = await ethHelperClient.sendEth(payload as SendEthParams)
      break
    case walletActions.sendSol:
      const solHelperClient = await getChainFusionClient(wallet)

      response = await solHelperClient.sendSol(payload as SolSendParams)
      break
    case walletActions.transferErc20:
      const chainFusionClient = await getChainFusionClient(wallet)

      response = await chainFusionClient.sendErc20(payload as SendErc20Params)
      break
    case walletActions.approveErc20:
      const cfClient = await getChainFusionClient(wallet)
      response = await cfClient.approveErc20(payload as ApproveErc20Params)
      break
    case walletActions.ethCallContract:
      const cClient = await getChainFusionClient(wallet)
      response = await cClient.ethCallContract(payload as EthCallContractParams)
      break
    case walletActions.customApproveErc20:
      const chainFClient = await getChainFusionClient(wallet)
      response = await chainFClient.customApproveErc20(payload as CustomApproveErc20Params)
      break
    case walletActions.depositErc20:
      const depositClient = await getChainFusionClient(wallet)
      response = depositClient.depositErc20(payload as ApproveErc20Params)
      break
    case walletActions.mintCkETH:
      const ethService = await getChainFusionClient(wallet)
      response = await ethService.mintCkEth(payload as MintCkEthParams)
      break
    case walletActions.transferBTC:
      const cfService = await getChainFusionClient(wallet)
      response = await cfService.sendBTC(payload as BTCSendParams)
      break
    case walletActions.transferOrdinal:
      const ordService = await getChainFusionClient(wallet)
      response = await ordService.sendOrdinal(payload as BTCSendOrdinalParams)
      break
    case walletActions.exportKeychain:
      response = await keychainManager.exportKeychain(payload.principal, payload.password)
      break
    case walletActions.exportPem:
      response = await keychainManager.exportPem(payload.principal, payload.password)
      break
    case walletActions.importPem:
      const { keypair, keyType } = payload

      const keyPairKeychain = await keychainManager.importKeychain({
        type: KeychainType.KeyPairKeychain,
        secretKey: keypair,
        keyType,
      })

      const importedPrincipals = await keyPairKeychain.getPrincipals()
      response = { principal: importedPrincipals[0].toString() }
      break
    case walletActions.burnXTC:
      response = await handleBurnXTC(payload, wallet)
      break
    case walletActions.getPrincipals:
      response = (await keychainManager.getPrincipals()).map((principal) => principal.toString())
      break
    case walletActions.getIdentity:
      const requestedIdentity = await keychainManager.getIdentity(payload.principal)

      if ('toJSON' in requestedIdentity && typeof requestedIdentity.toJSON === 'function') {
        response = requestedIdentity.toJSON()
      }

      break
    case walletActions.addAccount:
      const baseKeychain = await keychainManager.getKeychain(payload.principal)
      const newPrincipal = await keychainManager.addNewAccount(baseKeychain)

      response = newPrincipal.toString()
      break
    case walletActions.signMessage:
      response = await signMessage(payload as SignMessageParams)
      break
    default:
      response = { error: 'Unsupported' }
      break
  }

  return response
}
